import {regReducer} from '../../core';
import * as actions from './actions';
import * as r from 'ramda';
import * as m from './model';

// example state stored under `productConfigurator`
// const state = {
//     sortedCategoryIds: [1], // sorted list of categories
//     categories: {
//         1: {
//             id: 1,
//             name: 'Pasta',
//             minConfigs: 0,
//             maxConfigs: 1,
//             productConfigIds: [1, 2],
//             hasError: false,
//         },
//     },
//     productConfigs: {
//         1: {
//             id: 1,
//             name: 'standard',
//             image: null,
//             count: 0,
//             categoryId: 1,
//             isDough: true,
//             isSauce: false,
//         },
//         2: {
//             id: 2,
//             name: 'premium',
//             image: 'http://localhost/images/image.png',
//             count: 0,
//             categoryId: 1,
//             isDough: true,
//             isSauce: false,
//         },
//     },
//     variants: {
//         unlimited: {
//             id: 'unlimited',
//             name: 'eat all you can',
//             code: 'unlimited',
//             formattedPrice: '2,38 Kč',
//         },
//         limited: {
//             id: 'limited',
//             name: '3 + 1',
//             code: 'limited',
//             formattedPrice: '2,15 Kč',
//         },
//     },
//     configRestrictions: {
//         limited: [{categoryIds: [1], maxConfigs: 5, minConfigs: 1}],
//     },
//     namedProduct: {
//         label: '',
//         name: '',
//         savedName: '',
//         errors: ['Translated error message'],
//     },
//     unrolledCategories: new Set(),
//     selectedVariant: 'limited',
//     unrestrictedVariant: 'unlimited',
//     customPizza: true,
//     allowSave: true,
//     actions: {
//         name: {
//             url: 'http://localhost:8000/configured-product',
//         },
//     },
//     assets: {
//         img: {
//             dough: 'assets/shop/img/dough.png',
//         },
//     },
// };

const savedNameLens = r.lensPath([
    'productConfigurator',
    'namedProduct',
    'savedName',
]);

function anythingSelected(payload) {
    return r.any(
        r.propSatisfies(r.lt(0), 'count'),
        r.values(payload.productConfigs)
    );
}

regReducer(actions.INIT, (state, {payload}) => {
    const sortedCategoryIds = r.prop('sortedCategoryIds', payload);
    const firstProductConfigId = r.pipe(
        r.path(['categories', r.head(sortedCategoryIds), 'productConfigIds']),
        r.head
    )(payload);
    const categories = r.reduce(
        (categories, categoryId) =>
            r.assocPath([categoryId, 'enabled'], true, categories),
        r.prop('categories', payload),
        sortedCategoryIds
    );

    const productConfiguration = r.pipe(
        r.assocPath(['namedProduct', 'name'], ''),
        r.assocPath(['namedProduct', 'savedName'], ''),
        r.assocPath(['namedProduct', 'errors'], []),
        r.assoc('unrolledCategories', new Set()),
        r.assoc('categories', r.map(r.assoc('hasError', false), categories)),
        r.ifElse(
            (payload) => anythingSelected(payload) || !firstProductConfigId,
            r.identity,
            r.assocPath(['productConfigs', firstProductConfigId, 'count'], 1)
        )
    )(payload);

    return r.assoc('productConfigurator', productConfiguration, state);
});

regReducer(actions.ADD_PRODUCT_CONFIG, (state, {payload}) => {
    const categoryId = r.path(
        ['productConfigurator', 'productConfigs', payload, 'categoryId'],
        state
    );
    const category = m.category(state, categoryId);
    const updateProductConfigs = m.categoryShouldHaveExactly1Config(category)
        ? (state) =>
              r.reduce(
                  (state, pcId) =>
                      r.assocPath(
                          [
                              'productConfigurator',
                              'productConfigs',
                              pcId,
                              'count',
                          ],
                          0,
                          state
                      ),
                  state,
                  category.productConfigIds
              )
        : r.identity;

    const sortedCategoryIds = r.path(
        ['productConfigurator', 'sortedCategoryIds'],
        state
    );
    const position = r.indexOf(categoryId, sortedCategoryIds);
    const updateCategories =
        m.categoryConfigsCount(state, categoryId) + 1 >= category.minConfigs
            ? (state) =>
                  r.reduce(
                      (state, categoryId) =>
                          r.assocPath(
                              [
                                  'productConfigurator',
                                  'categories',
                                  categoryId,
                                  'enabled',
                              ],
                              true,
                              state
                          ),
                      state,
                      r.slice(position + 1, Infinity, sortedCategoryIds)
                  )
            : r.identity;

    const countLens = r.lensPath([
        'productConfigurator',
        'productConfigs',
        payload,
        'count',
    ]);

    const updateState = r.pipe(
        updateProductConfigs,
        updateCategories,
        r.set(savedNameLens, '')
    );

    const newState = r.over(countLens, r.inc, updateState(state));
    if (m.shouldNotifyAboutExceededLimit(newState)) {
        return r.assocPath(
            ['productConfigurator', 'showConfigurationLimitExceeded'],
            true,
            state
        );
    }
    if (!m.shouldAskToUpgradeVariant(newState)) {
        return newState;
    }

    return r.pipe(
        r.assocPath(['productConfigurator', 'showVariantUpgrade'], true),
        r.assocPath(
            ['productConfigurator', 'variantUpgrade', 'prevState'],
            state
        )
    )(newState);
});

regReducer(actions.REMOVE_PRODUCT_CONFIG, (state, {payload}) => {
    const countLens = r.lensPath([
        'productConfigurator',
        'productConfigs',
        payload,
        'count',
    ]);

    const newState = r.pipe(
        r.over(countLens, r.dec),
        r.set(savedNameLens, '')
    )(state);
    const newSelectedVariant = m.downgradedVariant(newState);

    return r.assocPath(
        ['productConfigurator', 'selectedVariant'],
        newSelectedVariant,
        newState
    );
});

regReducer(actions.TOGGLE_CATEGORY, (state, {payload}) => {
    const unrolledCategories = state.productConfigurator.unrolledCategories;
    const newState = r.reduce(
        (state, categoryId) =>
            r.assocPath(
                ['productConfigurator', 'categories', categoryId, 'hasError'],
                !m.isCategoryValid(state, categoryId),
                state
            ),
        state,
        unrolledCategories
    );

    if (unrolledCategories.has(payload)) {
        return r.assocPath(
            ['productConfigurator', 'unrolledCategories'],
            new Set(),
            newState
        );
    }

    return r.assocPath(
        ['productConfigurator', 'unrolledCategories'],
        new Set([payload]),
        newState
    );
});

regReducer(actions.SELECT_VARIANT, (state, {payload}) => {
    if (m.areVariantRestrictionsViolated(state, payload)) {
        return r.assocPath(
            ['productConfigurator', 'showVariantDowngradeInvalid'],
            true,
            state
        );
    }
    return r.assocPath(
        ['productConfigurator', 'selectedVariant'],
        payload,
        state
    );
});

regReducer(actions.HIDE_VARIANT_DOWNGRADE_INVALID, (state, {payload}) => {
    return r.dissocPath(
        ['productConfigurator', 'showVariantDowngradeInvalid'],
        state
    );
});

regReducer(actions.UPDATE_NAME, (state, {payload}) => {
    return r.assocPath(
        ['productConfigurator', 'namedProduct', 'name'],
        payload,
        state
    );
});

regReducer(actions.UPDATE_SAVED_NAME, (state, {payload}) => {
    return r.set(savedNameLens, payload, state);
});

regReducer(actions.UPDATE_NAME_ERRORS, (state, {payload}) => {
    return r.assocPath(
        ['productConfigurator', 'namedProduct', 'errors'],
        payload,
        state
    );
});

regReducer(actions.NAME_PRODUCT, (state) => {
    return r.assocPath(['productConfigurator', 'namingProduct'], true, state);
});

regReducer(actions.STOP_NAMING_PRODUCT, (state) => {
    return r.dissocPath(['productConfigurator', 'namingProduct'], state);
});

regReducer(actions.SHOW_CONFIGURATION_LIMIT_EXCEEDED, (state) => {
    return r.assocPath(
        ['productConfigurator', 'showConfigurationLimitExceeded'],
        true,
        state
    );
});

regReducer(actions.HIDE_CONFIGURATION_LIMIT_EXCEEDED, (state) => {
    return r.dissocPath(
        ['productConfigurator', 'showConfigurationLimitExceeded'],
        state
    );
});

regReducer(actions.SHOW_VARIANT_UPGRADE, (state) => {
    return r.assocPath(
        ['productConfigurator', 'showVariantUpgrade'],
        true,
        state
    );
});

regReducer(actions.HIDE_VARIANT_UPGRADE, (state) => {
    return r.pipe(
        r.dissocPath(['productConfigurator', 'showVariantUpgrade']),
        r.dissocPath(['productConfigurator', 'variantUpgrade'])
    )(state);
});

regReducer(actions.CANCEL_UPGRADING_VARIANT, (state) => {
    return r.path(
        ['productConfigurator', 'variantUpgrade', 'prevState'],
        state
    );
});
